import { useState, useEffect } from "react";
import { Trash2, Plus, ExternalLink, RefreshCw, Link2, FileText, Video } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  link: { icon: Link2, label: "Link", color: "bg-[#D6E6FA] text-[#1A5DAD]" },
  video: { icon: Video, label: "Video", color: "bg-[#FBE7C6] text-[#8B5E00]" },
  pdf: { icon: FileText, label: "PDF", color: "bg-[#D4EDDA] text-[#155724]" },
};

const TABS = [
  {
    id: "session",
    label: "Session Resources",
    desc: "Added per class session — tutors or admins after attendance is marked",
    badge: "Tutor & Admin",
    badgeColor: "bg-[#D6E6FA] text-[#1A5DAD]",
  },
  {
    id: "student",
    label: "Student Materials",
    desc: "Pinned resources for a specific student enrollment — visible immediately",
    badge: "Admin Only",
    badgeColor: "bg-[#FBE7C6] text-[#8B5E00]",
  },
  {
    id: "course",
    label: "Course Materials",
    desc: "Global resources for all students enrolled in a course — always visible",
    badge: "Admin Only",
    badgeColor: "bg-[#D4EDDA] text-[#155724]",
  },
];

// ── Shared resource table ───────────────────────────────────────────────
const ResourceTable = ({ resources, loading, onDelete, columns }) => {
  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };

  if (loading) return <div className="flex justify-center py-10"><div className="spinner" /></div>;
  if (resources.length === 0) return (
    <div className="text-center py-12 text-[#4A4A4A]">No resources added yet.</div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-[#0A0A0A]">
            {columns.map(c => (
              <th key={c} className="text-left py-3 px-2 font-bold text-xs uppercase tracking-wider">{c}</th>
            ))}
            <th className="py-3 px-2 w-12" />
          </tr>
        </thead>
        <tbody>
          {resources.map(r => {
            const cfg = TYPE_CONFIG[r.type] || TYPE_CONFIG.link;
            const TypeIcon = cfg.icon;
            return (
              <tr key={r.id} className="border-b border-[#0A0A0A]/10 hover:bg-[#F0EFEB]" data-testid={`resource-row-${r.id}`}>
                {r.student_name !== undefined && (
                  <td className="py-3 px-2 font-medium">{r.student_name}</td>
                )}
                {r.course_name !== undefined && (
                  <td className="py-3 px-2 font-medium">{r.course_name}</td>
                )}
                {r.week_number !== undefined && (
                  <td className="py-3 px-2">
                    {r.week_number != null ? <span className="font-bold">Wk {r.week_number}</span> : <span className="text-[#4A4A4A]">—</span>}
                  </td>
                )}
                <td className="py-3 px-2 max-w-[220px]">
                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#3C8DE4] hover:underline"
                    data-testid={`resource-link-${r.id}`}>
                    <span className="truncate">{r.title}</span>
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </td>
                <td className="py-3 px-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${cfg.color}`}>
                    <TypeIcon className="w-3 h-3" />{cfg.label}
                  </span>
                </td>
                {r.added_by_role !== undefined && (
                  <td className="py-3 px-2 capitalize text-[#4A4A4A]">{r.added_by_role}</td>
                )}
                <td className="py-3 px-2 text-[#4A4A4A] whitespace-nowrap">{formatDate(r.added_at)}</td>
                <td className="py-3 px-2">
                  <button onClick={() => onDelete(r.id)}
                    className="p-1.5 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded-lg transition-colors"
                    title="Delete" data-testid={`delete-resource-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Tab: Session Resources ────────────────────────────────────────────────
const SessionTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [resources, setResources] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [form, setForm] = useState({ title: "", type: "link", url: "" });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    loadResources();
    axios.get(`${API}/admin/all-enrollments`, { headers }).then(r => setEnrollments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedEnrollment) { setSessions([]); setSelectedSession(""); return; }
    setLoadingSessions(true);
    axios.get(`${API}/class-sessions/enrollment/${selectedEnrollment}`, { headers })
      .then(r => setSessions(r.data)).catch(() => {}).finally(() => setLoadingSessions(false));
  }, [selectedEnrollment]);

  const loadResources = () => {
    setLoading(true);
    axios.get(`${API}/admin/all-resources`, { headers })
      .then(r => setResources(r.data)).catch(() => toast.error("Failed to load resources"))
      .finally(() => setLoading(false));
  };

  const add = async () => {
    if (!selectedSession) return toast.error("Select a session/week");
    if (!form.title.trim() || !form.url.trim()) return toast.error("Title and URL required");
    setAdding(true);
    try {
      await axios.post(`${API}/session-resources`, {
        session_id: selectedSession, enrollment_id: selectedEnrollment,
        title: form.title, type: form.type, url: form.url
      }, { headers });
      toast.success("Resource added!");
      setForm({ title: "", type: "link", url: "" });
      loadResources();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/session-resources/${deleteTarget}`, { headers });
      toast.success("Deleted"); setDeleteTarget(null); loadResources();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = filter
    ? resources.filter(r => r.student_name?.toLowerCase().includes(filter.toLowerCase()) || r.title?.toLowerCase().includes(filter.toLowerCase()))
    : resources;

  return (
    <div className="space-y-6">
      <div className="brutalist-card p-6">
        <h3 className="font-bold text-base mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add Session Resource</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Student / Enrollment</label>
            <select value={selectedEnrollment} onChange={e => setSelectedEnrollment(e.target.value)}
              className="brutalist-input w-full mt-1" data-testid="session-enrollment-select">
              <option value="">— Select enrollment —</option>
              {enrollments.map(e => <option key={e.id} value={e.id}>{e.student_name} — {e.course_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Session / Week {loadingSessions && <span className="text-[#3C8DE4]"> (loading…)</span>}</label>
            <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)}
              disabled={!selectedEnrollment || loadingSessions} className="brutalist-input w-full mt-1" data-testid="session-week-select">
              <option value="">— Select session —</option>
              {sessions.map(s => <option key={s.id} value={s.id}>Week {s.week_number}{s.attended === true ? " ✓ Attended" : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Resource Title</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., Week 3 Grammar Sheet" className="brutalist-input w-full mt-1" data-testid="session-res-title" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">URL</label>
              <input type="url" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })}
                placeholder="https://..." className="brutalist-input w-full mt-1" data-testid="session-res-url" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="brutalist-input mt-1" data-testid="session-res-type">
                <option value="link">Link</option>
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </div>
        </div>
        <button onClick={add} disabled={adding} className="brutalist-btn-primary mt-4 flex items-center gap-2" data-testid="add-session-resource-btn">
          <Plus className="w-4 h-4" /> {adding ? "Adding…" : "Add Resource"}
        </button>
      </div>

      <div className="brutalist-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-bold text-base">All Session Resources <span className="text-[#4A4A4A] font-normal">({resources.length})</span></h3>
          <div className="flex items-center gap-2">
            <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder="Filter by student or title…" className="brutalist-input text-sm" data-testid="session-filter" />
            <button onClick={loadResources} className="p-2 hover:bg-[#D6E6FA] rounded-lg" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
        <ResourceTable resources={filtered} loading={loading} onDelete={setDeleteTarget}
          columns={["Student", "Week", "Title", "Type", "Added By", "Date"]} />
      </div>

      <ConfirmDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}
        title="Delete Resource?" description="This permanently removes the resource from the student's session."
        onConfirm={doDelete} destructive />
    </div>
  );
};

// ── Tab: Student Materials ─────────────────────────────────────────────────
const StudentTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [resources, setResources] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState("");
  const [form, setForm] = useState({ title: "", type: "link", url: "" });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    loadResources();
    axios.get(`${API}/admin/all-enrollments`, { headers }).then(r => setEnrollments(r.data)).catch(() => {});
  }, []);

  const loadResources = () => {
    setLoading(true);
    axios.get(`${API}/admin/all-student-resources`, { headers })
      .then(r => setResources(r.data)).catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  const add = async () => {
    if (!selectedEnrollment) return toast.error("Select a student enrollment");
    if (!form.title.trim() || !form.url.trim()) return toast.error("Title and URL required");
    setAdding(true);
    try {
      await axios.post(`${API}/student-resources`, {
        enrollment_id: selectedEnrollment, title: form.title, type: form.type, url: form.url
      }, { headers });
      toast.success("Material added!");
      setForm({ title: "", type: "link", url: "" });
      loadResources();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/student-resources/${deleteTarget}`, { headers });
      toast.success("Deleted"); setDeleteTarget(null); loadResources();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = filter
    ? resources.filter(r => r.student_name?.toLowerCase().includes(filter.toLowerCase()) || r.title?.toLowerCase().includes(filter.toLowerCase()))
    : resources;

  return (
    <div className="space-y-6">
      <div className="brutalist-card p-6">
        <h3 className="font-bold text-base mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add Material for a Student</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Student / Enrollment</label>
            <select value={selectedEnrollment} onChange={e => setSelectedEnrollment(e.target.value)}
              className="brutalist-input w-full mt-1" data-testid="student-enrollment-select">
              <option value="">— Select enrollment —</option>
              {enrollments.map(e => <option key={e.id} value={e.id}>{e.student_name} — {e.course_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Resource Title</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., Extra Practice Set" className="brutalist-input w-full mt-1" data-testid="student-res-title" />
          </div>
          <div className="flex gap-3 md:col-span-2">
            <div className="flex-1">
              <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">URL</label>
              <input type="url" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })}
                placeholder="https://..." className="brutalist-input w-full mt-1" data-testid="student-res-url" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="brutalist-input mt-1" data-testid="student-res-type">
                <option value="link">Link</option>
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </div>
        </div>
        <button onClick={add} disabled={adding} className="brutalist-btn-primary mt-4 flex items-center gap-2" data-testid="add-student-resource-btn">
          <Plus className="w-4 h-4" /> {adding ? "Adding…" : "Add Material"}
        </button>
      </div>

      <div className="brutalist-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-bold text-base">All Student Materials <span className="text-[#4A4A4A] font-normal">({resources.length})</span></h3>
          <div className="flex items-center gap-2">
            <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder="Filter…" className="brutalist-input text-sm" />
            <button onClick={loadResources} className="p-2 hover:bg-[#D6E6FA] rounded-lg"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
        <ResourceTable resources={filtered} loading={loading} onDelete={setDeleteTarget}
          columns={["Student", "Title", "Type", "Date"]} />
      </div>

      <ConfirmDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}
        title="Delete Material?" description="This removes this student's material permanently."
        onConfirm={doDelete} destructive />
    </div>
  );
};

// ── Tab: Course Materials ─────────────────────────────────────────────────
const CourseTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [resources, setResources] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [form, setForm] = useState({ title: "", type: "link", url: "", week: "" });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    loadResources();
    axios.get(`${API}/courses`, { headers }).then(r => setCourses(r.data)).catch(() => {});
  }, []);

  const loadResources = () => {
    setLoading(true);
    axios.get(`${API}/admin/all-course-resources`, { headers })
      .then(r => setResources(r.data)).catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  const add = async () => {
    if (!selectedCourse) return toast.error("Select a course");
    if (!form.title.trim() || !form.url.trim()) return toast.error("Title and URL required");
    setAdding(true);
    try {
      await axios.post(`${API}/course-resources`, {
        course_id: selectedCourse, title: form.title, type: form.type, url: form.url,
        week_number: form.week ? parseInt(form.week) : null
      }, { headers });
      toast.success("Course material added!");
      setForm({ title: "", type: "link", url: "", week: "" });
      loadResources();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/course-resources/${deleteTarget}`, { headers });
      toast.success("Deleted"); setDeleteTarget(null); loadResources();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = filter
    ? resources.filter(r => r.course_name?.toLowerCase().includes(filter.toLowerCase()) || r.title?.toLowerCase().includes(filter.toLowerCase()))
    : resources;

  return (
    <div className="space-y-6">
      <div className="brutalist-card p-6">
        <h3 className="font-bold text-base mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add Course-Wide Material</h3>
        <p className="text-sm text-[#4A4A4A] mb-4">This resource will appear on the dashboard of <strong>every student</strong> enrolled in the selected course.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Course</label>
            <select value={selectedCourse} onChange={e => setSelectedCourse(e.target.value)}
              className="brutalist-input w-full mt-1" data-testid="course-select">
              <option value="">— Select course —</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Resource Title</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., Beginner French Vocabulary List" className="brutalist-input w-full mt-1" data-testid="course-res-title" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Unlock After Week</label>
            <select value={form.week} onChange={e => setForm({ ...form, week: e.target.value })}
              className="brutalist-input w-full mt-1" data-testid="course-res-week">
              <option value="">Always visible (no lock)</option>
              {[1,2,3,4,5,6,7,8].map(w => (
                <option key={w} value={w}>Week {w} — unlocks after {w} attended class{w > 1 ? "es" : ""}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 md:col-span-2">
            <div className="flex-1">
              <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">URL</label>
              <input type="url" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })}
                placeholder="https://..." className="brutalist-input w-full mt-1" data-testid="course-res-url" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="brutalist-input mt-1" data-testid="course-res-type">
                <option value="link">Link</option>
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </div>
        </div>
        <button onClick={add} disabled={adding} className="brutalist-btn-primary mt-4 flex items-center gap-2" data-testid="add-course-resource-btn">
          <Plus className="w-4 h-4" /> {adding ? "Adding…" : "Add Course Material"}
        </button>
      </div>

      <div className="brutalist-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-bold text-base">All Course Materials <span className="text-[#4A4A4A] font-normal">({resources.length})</span></h3>
          <div className="flex items-center gap-2">
            <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder="Filter…" className="brutalist-input text-sm" />
            <button onClick={loadResources} className="p-2 hover:bg-[#D6E6FA] rounded-lg"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
        <ResourceTable resources={filtered} loading={loading} onDelete={setDeleteTarget}
          columns={["Course", "Week", "Title", "Type", "Date"]} />
      </div>

      <ConfirmDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}
        title="Delete Course Material?" description="This removes the material for all students in this course."
        onConfirm={doDelete} destructive />
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────
const AdminResources = () => {
  const [activeTab, setActiveTab] = useState("session");
  const token = localStorage.getItem("access_token");

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" data-testid="resources-title">Resources Manager</h1>
        <p className="text-[#4A4A4A]">Manage learning materials shared with students at three levels.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 mb-8 flex-wrap">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all flex items-center gap-2 ${
              activeTab === tab.id
                ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                : "border-[#0A0A0A]/30 hover:border-[#0A0A0A] bg-white"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
              activeTab === tab.id ? "bg-white/20 text-white" : tab.badgeColor
            }`}>
              {tab.badge}
            </span>
          </button>
        ))}
      </div>

      {/* Active tab description */}
      <p className="text-sm text-[#4A4A4A] mb-6 -mt-4">
        {TABS.find(t => t.id === activeTab)?.desc}
      </p>

      {activeTab === "session" && <SessionTab token={token} />}
      {activeTab === "student" && <StudentTab token={token} />}
      {activeTab === "course" && <CourseTab token={token} />}
    </div>
  );
};

export default AdminResources;
